import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  inject,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';

import { ButtonModule, DIALOG_DATA, DialogRef, EmptyComponent, IconModule, MultiSelectModule, OkDialog } from '@hglobal/fin-ui';
import { FormBuilder, FormControl } from '@ngneat/reactive-forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { environment } from '@kr/environment';
import { Transaction, TransactionStatus, WalletCard } from '@kr/models';
import { CurrencyBalanceFormatPipe } from '@kr/pipes';
import { DictionariesStore } from '@kr/store';

import { MassPayOutItemComponent } from './components';
import { WarningSaveBalanceComponent } from '../../../warning-save-balance/warning-save-balance.component';
import { MASS_PAYOUT_API, MassPayOutApiService } from '../../api';
import { MassPayoutStore } from '../../mass-pay-out.store';
import { MassPayOut, PayOutsFilters, PayOutsType } from '../../models';

@Component({
  imports: [
    TranslateModule,
    IconModule,
    ButtonModule,
    MassPayOutItemComponent,
    MultiSelectModule,
    NgClass,
    EmptyComponent,
    WarningSaveBalanceComponent,
    CurrencyBalanceFormatPipe,
  ],
  providers: [
    MassPayoutStore,
    {
      provide: MASS_PAYOUT_API,
      useClass: environment.useMocks ? MassPayOutApiService : MassPayOutApiService,
    },
  ],
  selector: 'kr-mass-pay-out-details',
  templateUrl: './mass-pay-out-details.component.html',
  styleUrls: ['./mass-pay-out-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MassPayOutDetailsComponent {
  readonly #translate = inject(TranslateService);
  readonly #dialogRef = inject(DialogRef);
  readonly #okDialog = inject(OkDialog);
  readonly #fb = inject(FormBuilder);
  readonly #store = inject(MassPayoutStore);
  readonly #dictionariesStore = inject(DictionariesStore);
  readonly #data = inject<{ wallet: WalletCard; massPayOut: MassPayOut }>(DIALOG_DATA);

  readonly failedTpl = viewChild<TemplateRef<any>>('failedTpl');

  readonly executed = signal<boolean>(false);
  readonly wallet = signal<WalletCard>(this.#data.wallet);
  readonly massPayOut = signal<MassPayOut>({ ...this.#data.massPayOut });
  readonly totalCount = signal<number>(this.#data.massPayOut.massPayOut.length);
  readonly ourEstimatedFee = signal<number>(this.#data.massPayOut.massPayOut.reduce((val, tr: Transaction) => val + tr.ourEstimatedFee, 0));

  readonly totalExecutedCount = this.#store.totalExecutedCount;
  readonly totalFailed = this.#store.totalFailed;

  readonly hasRiskScoreLogic = computed(() => {
    // if (!this.massPayOut()) {
    //   return null;
    // }
    // return !!this.massPayOut().massPayOut[0].riskScore;
    return false;
  });

  readonly networkBalance = computed(() => {
    return this.wallet().secondaryWalletCoinId ? this.massPayOut().networkBalance : this.wallet().balanceAvailable;
  });

  networkCoinName = computed(() => {
    return this.wallet().secondaryWalletCoin ? this.wallet().secondaryWalletCoin.symbol : this.wallet().coin.symbol;
  });

  readonly errMessage = signal('');

  readonly filters = signal<PayOutsFilters>({ score: [] });

  readonly isCryptoBanco = this.#dictionariesStore.isCryptoBanco;

  filtersForm = this.#fb.group({
    score: new FormControl<PayOutsType[]>(this.filters().score),
  });

  readonly payOutOptions = [
    { id: 'safe', name: this.#translate.instant('massPayOutDetails.filter.score.safe') },
    { id: 'risk', name: this.#translate.instant('massPayOutDetails.filter.score.risk') },
  ];

  readonly filteredMassPayOut = computed(() => {
    let payOuts = [...this.massPayOut().massPayOut];
    if (!payOuts.length) {
      return this.massPayOut();
    }

    const filters = this.filters();
    const payOutsFilter: PayOutsType[] = filters.score;

    if (!payOutsFilter) {
      return this.massPayOut();
    }

    payOuts = this.#applyFilter(payOuts, payOutsFilter);
    const fee = payOuts[0]?.fee ?? 0;
    const totalFee = (payOuts.reduce((sum, payOut) => sum + Number(payOut.fee), 0) + Number(fee)).toString();
    const totalAmount = payOuts.reduce((sum, payOut) => sum + Number(payOut.amount), 0).toString();

    return {
      itemsCount: payOuts.length,
      massPayOut: payOuts,
      totalFee,
      totalAmount,
      balanceAvailable: this.massPayOut().balanceAvailable,
      networkBalance: this.massPayOut().networkBalance,
    };
  });

  readonly isExecuteDisabled = computed(() => {
    return (
      (this.hasRiskScoreLogic() && this.filteredMassPayOut().massPayOut.some(payOut => !payOut.riskScorePassed)) ||
      this.filteredMassPayOut().massPayOut.length === 0 ||
      +this.filteredMassPayOut().totalAmount > +this.massPayOut().balanceAvailable
    );
  });

  readonly filteredTotalCount = computed(() => this.filteredMassPayOut().massPayOut?.length);

  readonly totalCountEffect = effect(() => {
    const totalCount = this.filteredMassPayOut().massPayOut?.length;
    untracked(() => this.totalCount.set(totalCount));
  });

  readonly executeMassPayoutSuccessEffect = effect(() => {
    const isExecuted = this.#store.isExecuteMassPayoutSuccess();
    if (isExecuted) {
      this.executed.set(true);
      untracked(() => {
        const data = this.#store.massPayOutExecuted();
        if (data) {
          this.massPayOut.set({
            ...this.massPayOut(),
            massPayOut: this.#store.massPayOutExecuted(),
          });
        }
      });
    }
  });

  readonly executeMassPayoutErrorEffect = effect(() => {
    const error = this.#store.executeMassPayoutError();
    if (error) {
      this.errMessage.set(error?.originalError.error.error_description || 'Unknown error');
      this.openFailedDialog();
    }
  });

  @HostBinding('class.kr-mass-pay-out-details_executed')
  get executedClass() {
    return this.executed();
  }

  get columnNames() {
    return this.executed()
      ? ['from', 'to', 'comment', 'status', 'fee', 'amount']
      : this.hasRiskScoreLogic()
        ? ['from', 'to', 'comment', 'fee', 'amount', 'score']
        : ['from', 'to', 'comment', 'fee', 'amount'];
  }

  get header() {
    return this.wallet().name + '/' + this.#translate.instant('massPayOutDetails.title');
  }

  get downloadCsvBtnText(): string {
    const config = this.#store.downloadConfig;

    return this.#translate.instant(config.translationKey);
  }

  get withRiskScoreClass(): string {
    return this.hasRiskScoreLogic() && !this.executed() ? 'with-risk-score' : '';
  }

  execute(): void {
    if (environment.useMocks) {
      this.filteredMassPayOut().massPayOut.map((item, i) => ({
        ...item,
        status: <TransactionStatus>(i % 2 ? 'completed' : 'rejected'),
      }));
      return;
    }

    const walletId = this.wallet().id;
    const orgId = this.wallet().organizationId;
    const body = this.filteredMassPayOut();

    this.#store.executeMassPayout({ body, walletId, orgId });
  }

  openFailedDialog(): void {
    this.#okDialog
      .open({
        headerKey: this.header,
        contentTpl: this.failedTpl(),
        noOk: true,
        noCancel: true,
      })
      .afterClosed()
      .subscribe(() => this.#dialogRef.close(false));
  }

  close(): void {
    this.#dialogRef.close(this.executed());
  }

  applyFilters(): void {
    this.filtersForm.markAsUntouched();
    this.filters.set(this.filtersForm.value);
  }

  downloadCsv(): void {
    if (environment.useMocks) {
      return;
    }

    const body = {
      rows: this.filteredMassPayOut().massPayOut.map(item => ({
        address: item.addressOut,
        amount: item.amount,
        comment: item.comment,
      })),
    };
    const orgId = this.wallet().organizationId;
    this.#store.downloadCsv({ body, filters: this.filters(), orgId });
  }

  #applyFilter(payOuts: Transaction[], scoreFilter: PayOutsType[]): Transaction[] {
    if (!scoreFilter || scoreFilter.length === 0) {
      return payOuts;
    }

    const includeRisk = scoreFilter.includes(PayOutsType.RISK);
    const includeSafe = scoreFilter.includes(PayOutsType.SAFE);

    return payOuts.filter(payOut => {
      if (includeRisk && includeSafe) {
        return true;
      }
      if (includeRisk) {
        return !payOut.riskScorePassed;
      }
      if (includeSafe) {
        return payOut.riskScorePassed;
      }
      return false;
    });
  }
}
